import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Upload, 
  FileSpreadsheet, 
  Check, 
  X, 
  AlertTriangle, 
  Link as LinkIcon,
  ArrowUpRight,
  ArrowDownRight,
  Building,
  RefreshCw,
  Trash2,
  Search,
  Settings,
  Zap,
  Save,
  Table,
  CheckSquare,
  Sparkles,
  Plus,
  Edit,
  Power,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from "lucide-react";
import axios from "axios";

const BankImport = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [filterMatched, setFilterMatched] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");
  
  // Column filters state
  const [columnFilters, setColumnFilters] = useState({
    date: "",
    description: "",
    amount: "",
    status: "",
    suggestion: ""
  });
  
  // Multi-select state
  const [selectedTransactions, setSelectedTransactions] = useState(new Set());
  const [bulkMatchOpen, setBulkMatchOpen] = useState(false);
  const [bulkAccount, setBulkAccount] = useState("");
  const [bulkDescription, setBulkDescription] = useState("");
  const [createRule, setCreateRule] = useState(false);
  const [ruleName, setRuleName] = useState("");
  const [rulePattern, setRulePattern] = useState("");
  
  // Column mapping state
  const [columnMappingOpen, setColumnMappingOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({
    date_column: "",
    amount_column: "",
    description_column: "",
    balance_column: "",
    reference_column: ""
  });
  const [templateName, setTemplateName] = useState("");
  const [decimalSeparator, setDecimalSeparator] = useState(",");
  const [dateFormat, setDateFormat] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  
  // Automation rules state
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const [automationRules, setAutomationRules] = useState([]);
  const [editingRule, setEditingRule] = useState(null);
  const [newRule, setNewRule] = useState({
    name: "",
    pattern: "",
    pattern_type: "contains",
    account_number: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [txnRes, accountsRes, invoicesRes, summaryRes, templatesRes, rulesRes] = await Promise.all([
        axios.get(`${API}/bank/transactions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/accounts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/sales-invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/bank/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/bank/templates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/bank/automation-rules`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTransactions(txnRes.data);
      setAccounts(accountsRes.data);
      setInvoices(invoicesRes.data.filter(i => i.status !== 'paid'));
      setSummary(summaryRes.data);
      setTemplates(templatesRes.data);
      setAutomationRules(rulesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Kunde inte hämta data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setPendingFile(file);
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/bank/preview-columns`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setPreviewData(response.data);
      setColumnMapping({
        date_column: response.data.detected_mapping.date_column || "",
        amount_column: response.data.detected_mapping.amount_column || "",
        description_column: response.data.detected_mapping.description_column || "",
        balance_column: response.data.detected_mapping.balance_column || "",
        reference_column: response.data.detected_mapping.reference_column || ""
      });
      setColumnMappingOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte läsa filen");
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      setColumnMapping({
        date_column: template.date_column || "",
        amount_column: template.amount_column || "",
        description_column: template.description_column || "",
        balance_column: template.balance_column || "",
        reference_column: template.reference_column || ""
      });
      setDecimalSeparator(template.decimal_separator || ",");
      setDateFormat(template.date_format || "");
      setTemplateName(template.name);
    }
  };

  const handleImportWithMapping = async () => {
    if (!pendingFile || !columnMapping.date_column || !columnMapping.amount_column) {
      toast.error("Välj datum- och beloppkolumner");
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', pendingFile);
      formData.append('date_column', columnMapping.date_column);
      formData.append('amount_column', columnMapping.amount_column);
      if (columnMapping.description_column) formData.append('description_column', columnMapping.description_column);
      if (columnMapping.balance_column) formData.append('balance_column', columnMapping.balance_column);
      if (columnMapping.reference_column) formData.append('reference_column', columnMapping.reference_column);
      if (templateName) formData.append('template_name', templateName);
      formData.append('decimal_separator', decimalSeparator);
      if (dateFormat) formData.append('date_format', dateFormat);
      
      const response = await axios.post(`${API}/bank/import-with-mapping`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`${response.data.total_transactions} transaktioner importerade`);
      setColumnMappingOpen(false);
      setPendingFile(null);
      setPreviewData(null);
      setTemplateName("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import misslyckades");
    } finally {
      setUploading(false);
    }
  };

  const handleMatch = async (transactionId, invoiceId, accountNumber, createEntry = true) => {
    try {
      await axios.post(`${API}/bank/transactions/${transactionId}/match`, {
        transaction_id: transactionId,
        invoice_id: invoiceId,
        account_number: accountNumber,
        create_journal_entry: createEntry
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Transaktion matchad");
      setMatchDialogOpen(false);
      setSelectedTransaction(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Matchning misslyckades");
    }
  };

  const handleBulkMatch = async () => {
    if (!bulkAccount || selectedTransactions.size === 0) {
      toast.error("Välj konto och transaktioner");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/bank/bulk-match`, {
        transaction_ids: Array.from(selectedTransactions),
        account_number: bulkAccount,
        description: bulkDescription || null,
        create_automation_rule: createRule,
        rule_name: ruleName || null,
        rule_pattern: rulePattern || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setBulkMatchOpen(false);
      setSelectedTransactions(new Set());
      setBulkAccount("");
      setBulkDescription("");
      setCreateRule(false);
      setRuleName("");
      setRulePattern("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk-matchning misslyckades");
    }
  };

  const handleReconcileAll = async () => {
    try {
      const response = await axios.post(`${API}/bank/reconcile-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.reconciled_count > 0) {
        toast.success(`${response.data.reconciled_count} transaktioner avstämda automatiskt`);
        fetchData();
      } else {
        toast.info("Inga transaktioner kunde avstämmas automatiskt");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Avstämning misslyckades");
    }
  };

  const handleDelete = async (transactionId) => {
    try {
      await axios.delete(`${API}/bank/transactions/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Transaktion borttagen");
      fetchData();
    } catch (error) {
      toast.error("Kunde inte ta bort transaktion");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTransactions.size === 0) return;
    
    let deletedCount = 0;
    let failedCount = 0;
    
    for (const txnId of selectedTransactions) {
      try {
        await axios.delete(`${API}/bank/transactions/${txnId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        deletedCount++;
      } catch (error) {
        failedCount++;
        console.error(`Failed to delete transaction ${txnId}:`, error);
      }
    }
    
    if (deletedCount > 0 && failedCount === 0) {
      toast.success(`${deletedCount} transaktioner borttagna`);
    } else if (deletedCount > 0 && failedCount > 0) {
      toast.warning(`${deletedCount} borttagna, ${failedCount} kunde inte tas bort`);
    } else {
      toast.error("Kunde inte ta bort några transaktioner");
    }
    
    setSelectedTransactions(new Set());
    fetchData();
  };

  const handleQuickMatch = async (transactionId, accountNumber) => {
    try {
      await axios.post(`${API}/bank/transactions/${transactionId}/match`, {
        transaction_id: transactionId,
        invoice_id: null,
        account_number: accountNumber,
        create_journal_entry: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Transaktion bokförd");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bokföring misslyckades");
    }
  };

  const handleBulkAcceptSuggestions = async () => {
    if (selectedTransactions.size === 0) return;
    
    // Get transactions with suggestions that are not already matched
    const txnsWithSuggestions = transactions.filter(t => 
      selectedTransactions.has(t.id) && t.suggested_account && !t.matched
    );
    
    if (txnsWithSuggestions.length === 0) {
      toast.info("Inga valda transaktioner har förslag att acceptera");
      return;
    }
    
    let matchedCount = 0;
    let failedCount = 0;
    let skippedCount = selectedTransactions.size - txnsWithSuggestions.length;
    
    for (const txn of txnsWithSuggestions) {
      try {
        await axios.post(`${API}/bank/transactions/${txn.id}/match`, {
          transaction_id: txn.id,
          invoice_id: null,
          account_number: txn.suggested_account,
          create_journal_entry: true
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        matchedCount++;
      } catch (error) {
        failedCount++;
        console.error(`Failed to match transaction ${txn.id}:`, error);
      }
    }
    
    // Build result message
    let message = `${matchedCount} bokförda`;
    if (skippedCount > 0) message += `, ${skippedCount} hoppade över (redan matchade/saknar förslag)`;
    if (failedCount > 0) message += `, ${failedCount} misslyckades`;
    
    if (matchedCount > 0 && failedCount === 0) {
      toast.success(message);
    } else if (matchedCount > 0 && failedCount > 0) {
      toast.warning(message);
    } else if (matchedCount === 0 && skippedCount > 0) {
      toast.info(message);
    } else {
      toast.error("Kunde inte bokföra några transaktioner");
    }
    
    setSelectedTransactions(new Set());
    fetchData();
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/bank/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Mall borttagen");
      fetchData();
    } catch (error) {
      toast.error("Kunde inte ta bort mall");
    }
  };

  const handleSaveRule = async () => {
    if (!newRule.name || !newRule.pattern || !newRule.account_number) {
      toast.error("Fyll i alla obligatoriska fält");
      return;
    }
    
    try {
      if (editingRule) {
        await axios.put(`${API}/bank/automation-rules/${editingRule.id}`, newRule, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Regel uppdaterad");
      } else {
        await axios.post(`${API}/bank/automation-rules`, newRule, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Regel skapad");
      }
      setEditingRule(null);
      setNewRule({ name: "", pattern: "", pattern_type: "contains", account_number: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte spara regel");
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm("Ta bort denna regel?")) return;
    try {
      await axios.delete(`${API}/bank/automation-rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Regel borttagen");
      fetchData();
    } catch (error) {
      toast.error("Kunde inte ta bort regel");
    }
  };

  const toggleRuleActive = async (rule) => {
    try {
      await axios.put(`${API}/bank/automation-rules/${rule.id}`, {
        ...rule,
        is_active: !rule.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      toast.error("Kunde inte uppdatera regel");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Get sort icon for column header
  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown size={14} className="ml-1 text-slate-400" />;
    }
    return sortDirection === "asc" 
      ? <ArrowUp size={14} className="ml-1 text-indigo-600" />
      : <ArrowDown size={14} className="ml-1 text-indigo-600" />;
  };

  // Update column filter
  const updateColumnFilter = (column, value) => {
    setColumnFilters(prev => ({ ...prev, [column]: value }));
  };

  const filteredTransactions = transactions
    .filter(t => {
      // Status filter (matched/unmatched dropdown)
      const matchesFilter = filterMatched === "all" || 
        (filterMatched === "matched" && t.matched) ||
        (filterMatched === "unmatched" && !t.matched);
      
      // Global search
      const matchesSearch = !searchTerm || 
        t.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.counterpart?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Column-specific filters
      const matchesDateFilter = !columnFilters.date || 
        t.date?.toLowerCase().includes(columnFilters.date.toLowerCase());
      
      const matchesDescFilter = !columnFilters.description || 
        t.description?.toLowerCase().includes(columnFilters.description.toLowerCase());
      
      const matchesAmountFilter = !columnFilters.amount || 
        String(t.amount).includes(columnFilters.amount);
      
      const matchesStatusFilter = !columnFilters.status || 
        (columnFilters.status.toLowerCase() === "matchad" && t.matched) ||
        (columnFilters.status.toLowerCase() === "omatchad" && !t.matched) ||
        (t.matched && "matchad".includes(columnFilters.status.toLowerCase())) ||
        (!t.matched && "omatchad".includes(columnFilters.status.toLowerCase()));
      
      const matchesSuggestionFilter = !columnFilters.suggestion || 
        t.suggested_account?.toLowerCase().includes(columnFilters.suggestion.toLowerCase()) ||
        t.suggested_account_name?.toLowerCase().includes(columnFilters.suggestion.toLowerCase()) ||
        t.matched_invoice_number?.toLowerCase().includes(columnFilters.suggestion.toLowerCase());
      
      return matchesFilter && matchesSearch && matchesDateFilter && 
             matchesDescFilter && matchesAmountFilter && matchesStatusFilter && matchesSuggestionFilter;
    })
    .sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case "date":
          aVal = a.date || "";
          bVal = b.date || "";
          break;
        case "description":
          aVal = (a.description || "").toLowerCase();
          bVal = (b.description || "").toLowerCase();
          break;
        case "amount":
          aVal = a.amount || 0;
          bVal = b.amount || 0;
          break;
        case "status":
          aVal = a.matched ? 1 : 0;
          bVal = b.matched ? 1 : 0;
          break;
        case "suggestion":
          aVal = a.suggested_account || a.matched_invoice_number || "";
          bVal = b.suggested_account || b.matched_invoice_number || "";
          break;
        default:
          aVal = a.date || "";
          bVal = b.date || "";
      }
      
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const unmatchedCount = transactions.filter(t => !t.matched).length;
  const matchedCount = transactions.filter(t => t.matched).length;
  const unmatchedTransactions = filteredTransactions.filter(t => !t.matched);

  const toggleSelectAll = () => {
    if (selectedTransactions.size === unmatchedTransactions.length) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(new Set(unmatchedTransactions.map(t => t.id)));
    }
  };

  const toggleTransaction = (id) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedTransactions(newSelected);
  };

  // Suggest rule pattern from selected transactions
  const suggestRulePattern = () => {
    const selectedTxns = transactions.filter(t => selectedTransactions.has(t.id));
    if (selectedTxns.length > 0) {
      // Find common words in descriptions
      const words = selectedTxns.map(t => t.description.toLowerCase().split(/\s+/));
      const commonWords = words.reduce((common, txnWords, index) => {
        if (index === 0) return txnWords;
        return common.filter(word => txnWords.includes(word));
      }, []);
      
      if (commonWords.length > 0) {
        setRulePattern(commonWords.join(' '));
      } else if (selectedTxns.length === 1) {
        setRulePattern(selectedTxns[0].description.toLowerCase());
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('bankImport.title')}</h1>
          <p className="text-slate-600">{t('bankImport.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => setRulesDialogOpen(true)}
            data-testid="automation-rules-btn"
          >
            <Zap size={16} className="mr-2" />
            Automatiseringsregler ({automationRules.filter(r => r.is_active).length})
          </Button>
          {unmatchedCount > 0 && (
            <Button 
              variant="outline" 
              onClick={handleReconcileAll}
              className="border-green-500 text-green-600 hover:bg-green-50"
              data-testid="reconcile-all-btn"
            >
              <Check size={16} className="mr-2" />
              {t('bankImport.reconcileAll')} ({unmatchedCount})
            </Button>
          )}
          <input
            type="file"
            accept=".csv,.xlsx,.xls,.sta,.mt940,.940,.txt"
            onChange={handleFileSelect}
            className="hidden"
            id="bank-file-upload"
            disabled={uploading}
          />
          <label htmlFor="bank-file-upload">
            <Button asChild className="bg-indigo-600 hover:bg-indigo-700" disabled={uploading}>
              <span>
                {uploading ? (
                  <RefreshCw className="animate-spin mr-2" size={16} />
                ) : (
                  <Upload size={16} className="mr-2" />
                )}
                {uploading ? t('bankImport.importing') : t('bankImport.importFile')}
              </span>
            </Button>
          </label>
        </div>
      </div>

      {/* Summary Cards - Improved Design */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-indigo-500 hover:shadow-md transition-shadow">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Totalt</p>
                <p className="text-2xl font-bold text-slate-900">{summary?.total_transactions || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                <FileSpreadsheet className="text-indigo-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-green-500 hover:shadow-md transition-shadow">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Bokförda</p>
                <p className="text-2xl font-bold text-green-600">{matchedCount}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Check className="text-green-600" size={24} />
              </div>
            </div>
            {summary?.total_transactions > 0 && (
              <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 transition-all duration-500"
                  style={{ width: `${(matchedCount / summary.total_transactions) * 100}%` }}
                />
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-amber-500 hover:shadow-md transition-shadow">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Ej bokförda</p>
                <p className="text-2xl font-bold text-amber-600">{unmatchedCount}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="text-amber-600" size={24} />
              </div>
            </div>
            {unmatchedCount > 0 && (
              <p className="mt-2 text-xs text-amber-600">
                Klicka "Bokför" eller välj konto manuellt
              </p>
            )}
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-purple-500 hover:shadow-md transition-shadow">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Auto-regler</p>
                <p className="text-2xl font-bold text-purple-600">{automationRules.filter(r => r.is_active).length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Zap className="text-purple-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions Bar */}
      {selectedTransactions.size > 0 && (
        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckSquare className="text-indigo-600" size={20} />
                <span className="font-medium text-indigo-900">
                  {selectedTransactions.size} transaktioner valda
                </span>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedTransactions(new Set())}
                >
                  Avmarkera alla
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={handleBulkDelete}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 size={14} className="mr-2" />
                  Ta bort valda
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  className="text-green-600 border-green-300 hover:bg-green-50"
                  onClick={handleBulkAcceptSuggestions}
                  data-testid="bulk-accept-suggestions-btn"
                >
                  <Sparkles size={14} className="mr-2" />
                  Acceptera förslag
                </Button>
                <Button 
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => setBulkMatchOpen(true)}
                  data-testid="bulk-match-btn"
                >
                  <LinkIcon size={14} className="mr-2" />
                  Bokför valda
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transactions Table - Excel Style */}
      <Card className="overflow-hidden border border-slate-300">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">{t('bankImport.transactions')}</CardTitle>
            <div className="flex gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <Input 
                  placeholder={t('bankImport.searchTransactions')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-64 h-9"
                />
              </div>
              <Select value={filterMatched} onValueChange={setFilterMatched}>
                <SelectTrigger className="w-40 h-9">
                  <SelectValue placeholder={t('common.filter')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  <SelectItem value="matched">{t('bankImport.matched')}</SelectItem>
                  <SelectItem value="unmatched">{t('bankImport.unmatched')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredTransactions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileSpreadsheet className="mx-auto mb-3 text-slate-300" size={48} />
              <p>{t('common.noData')}</p>
              <p className="text-sm">{t('bankImport.importFile')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[900px]">
                <colgroup>
                  <col style={{ width: '40px' }} />
                  <col style={{ width: '90px' }} />
                  <col />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '180px' }} />
                  <col style={{ width: '140px' }} />
                </colgroup>
                <thead className="bg-slate-100 border-b border-slate-300">
                  <tr style={{ height: '36px' }}>
                    <th className="px-2 py-2 border-r border-slate-200">
                      <Checkbox 
                        checked={selectedTransactions.size === unmatchedTransactions.length && unmatchedTransactions.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide border-r border-slate-200">
                      <button className="flex items-center gap-1 hover:text-indigo-600" onClick={() => handleSort("date")}>
                        {sortColumn === 'date' ? (sortDirection === 'asc' ? <ArrowUp size={12}/> : <ArrowDown size={12}/>) : <ArrowUpDown size={12} className="text-slate-400"/>}
                        Datum
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide border-r border-slate-200">
                      <button className="flex items-center gap-1 hover:text-indigo-600" onClick={() => handleSort("description")}>
                        {sortColumn === 'description' ? (sortDirection === 'asc' ? <ArrowUp size={12}/> : <ArrowDown size={12}/>) : <ArrowUpDown size={12} className="text-slate-400"/>}
                        Beskrivning
                      </button>
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide border-r border-slate-200">
                      <button className="flex items-center gap-1 justify-end hover:text-indigo-600 w-full" onClick={() => handleSort("amount")}>
                        {sortColumn === 'amount' ? (sortDirection === 'asc' ? <ArrowUp size={12}/> : <ArrowDown size={12}/>) : <ArrowUpDown size={12} className="text-slate-400"/>}
                        Belopp
                      </button>
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-slate-600 uppercase tracking-wide border-r border-slate-200">
                      <button className="flex items-center gap-1 justify-center hover:text-indigo-600 w-full" onClick={() => handleSort("status")}>
                        {sortColumn === 'status' ? (sortDirection === 'asc' ? <ArrowUp size={12}/> : <ArrowDown size={12}/>) : <ArrowUpDown size={12} className="text-slate-400"/>}
                        Status
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide border-r border-slate-200">
                      Konto / Förslag
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-slate-600 uppercase tracking-wide">
                      Snabbbok
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((txn, index) => (
                    <tr 
                      key={txn.id} 
                      className={`h-10 border-b border-slate-200 hover:bg-indigo-50/50 transition-colors ${
                        index % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'
                      } ${selectedTransactions.has(txn.id) ? 'bg-indigo-50' : ''}`}
                    >
                      <td className="px-2 py-1 border-r border-slate-200 text-center">
                        {!txn.matched && (
                          <Checkbox 
                            checked={selectedTransactions.has(txn.id)}
                            onCheckedChange={() => toggleTransaction(txn.id)}
                            data-testid={`txn-checkbox-${txn.id}`}
                          />
                        )}
                      </td>
                      <td className="px-3 py-1 border-r border-slate-200 font-mono text-sm text-slate-600">
                        {txn.date?.slice(2) || txn.date}
                      </td>
                      <td className="px-3 py-1 border-r border-slate-200 text-sm text-slate-800 truncate">
                        <div className="truncate" title={txn.description}>{txn.description}</div>
                        {txn.reference && (
                          <div className="text-xs text-slate-400 font-mono truncate">Ref: {txn.reference}</div>
                        )}
                      </td>
                      <td className={`px-3 py-1 border-r border-slate-200 font-mono text-sm text-right font-medium ${
                        txn.amount > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        <span className="flex items-center justify-end gap-1">
                          {txn.amount > 0 ? <ArrowDownRight size={12} /> : <ArrowUpRight size={12} />}
                          {formatCurrency(txn.amount)}
                        </span>
                      </td>
                      <td className="px-3 py-1 border-r border-slate-200 text-center">
                        {txn.matched ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            <Check size={10} className="mr-1" />
                            Bokförd
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-300 text-amber-700 text-xs">
                            <AlertTriangle size={10} className="mr-1" />
                            Ej bokförd
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-1 border-r border-slate-200 text-sm">
                        {txn.matched_invoice_number ? (
                          <span className="text-indigo-600 font-mono text-xs flex items-center gap-1">
                            <LinkIcon size={10} />
                            {txn.matched_invoice_number}
                          </span>
                        ) : txn.suggested_account ? (
                          <span className="text-slate-600 text-xs">
                            {txn.suggested_account} - {txn.suggested_account_name?.slice(0, 15)}...
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs italic">Inget förslag</span>
                        )}
                      </td>
                      <td className="px-3 py-1 text-center">
                        {!txn.matched && (
                          <div className="flex items-center justify-center gap-1">
                            {txn.suggested_account ? (
                              <Button
                                size="sm"
                                className="h-7 px-2 bg-green-600 hover:bg-green-700 text-xs"
                                onClick={() => handleQuickMatch(txn.id, txn.suggested_account)}
                                title={`Bokför på ${txn.suggested_account}`}
                                data-testid={`quick-book-${txn.id}`}
                              >
                                <Check size={12} className="mr-1" />
                                Bokför
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs"
                                onClick={() => { setSelectedTransaction(txn); setMatchDialogOpen(true); }}
                                data-testid={`manual-book-${txn.id}`}
                              >
                                <Plus size={12} className="mr-1" />
                                Välj konto
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(txn.id)}
                              title="Ta bort"
                            >
                              <Trash2 size={12} />
                            </Button>
                          </div>
                        )}
                        {txn.matched && (
                          <span className="text-green-600 text-xs flex items-center justify-center gap-1">
                            <Check size={12} />
                            Klar
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Column Mapping Dialog */}
      <Dialog open={columnMappingOpen} onOpenChange={setColumnMappingOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Table size={20} />
              Kolumnmappning
            </DialogTitle>
            <DialogDescription>
              Välj vilka kolumner som innehåller datum, belopp och beskrivning. Du kan spara detta som en mall.
            </DialogDescription>
          </DialogHeader>
          
          {previewData && (
            <div className="space-y-6">
              {/* Template Selection */}
              {templates.length > 0 && (
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                  <Label className="whitespace-nowrap">Använd mall:</Label>
                  <Select onValueChange={handleTemplateSelect}>
                    <SelectTrigger className="w-64">
                      <SelectValue placeholder="Välj sparad mall..." />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Column Mapping */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-red-600">Datumkolumn *</Label>
                  <Select value={columnMapping.date_column} onValueChange={(v) => setColumnMapping({...columnMapping, date_column: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Välj kolumn..." />
                    </SelectTrigger>
                    <SelectContent>
                      {previewData.columns.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-red-600">Beloppkolumn *</Label>
                  <Select value={columnMapping.amount_column} onValueChange={(v) => setColumnMapping({...columnMapping, amount_column: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Välj kolumn..." />
                    </SelectTrigger>
                    <SelectContent>
                      {previewData.columns.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Beskrivningskolumn</Label>
                  <Select value={columnMapping.description_column || "_none_"} onValueChange={(v) => setColumnMapping({...columnMapping, description_column: v === "_none_" ? "" : v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Välj kolumn..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">Ingen</SelectItem>
                      {previewData.columns.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Saldokolumn</Label>
                  <Select value={columnMapping.balance_column || "_none_"} onValueChange={(v) => setColumnMapping({...columnMapping, balance_column: v === "_none_" ? "" : v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Välj kolumn..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">Ingen</SelectItem>
                      {previewData.columns.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Referenskolumn</Label>
                  <Select value={columnMapping.reference_column || "_none_"} onValueChange={(v) => setColumnMapping({...columnMapping, reference_column: v === "_none_" ? "" : v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Välj kolumn..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">Ingen</SelectItem>
                      {previewData.columns.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Decimalseparator</Label>
                  <Select value={decimalSeparator} onValueChange={setDecimalSeparator}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value=",">Komma (,)</SelectItem>
                      <SelectItem value=".">Punkt (.)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Data Preview */}
              <div className="space-y-2">
                <Label>Förhandsvisning ({previewData.row_count} rader)</Label>
                <div className="border rounded-lg overflow-x-auto max-h-48">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        {previewData.columns.map(col => (
                          <th key={col} className={`p-2 text-left font-medium whitespace-nowrap ${
                            col === columnMapping.date_column ? 'bg-blue-100' :
                            col === columnMapping.amount_column ? 'bg-green-100' :
                            col === columnMapping.description_column ? 'bg-purple-100' : ''
                          }`}>
                            {col}
                            {col === columnMapping.date_column && <span className="ml-1 text-blue-600">(Datum)</span>}
                            {col === columnMapping.amount_column && <span className="ml-1 text-green-600">(Belopp)</span>}
                            {col === columnMapping.description_column && <span className="ml-1 text-purple-600">(Beskr.)</span>}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.sample_rows.map((row, i) => (
                        <tr key={i} className="border-t">
                          {previewData.columns.map(col => (
                            <td key={col} className={`p-2 whitespace-nowrap ${
                              col === columnMapping.date_column ? 'bg-blue-50' :
                              col === columnMapping.amount_column ? 'bg-green-50' :
                              col === columnMapping.description_column ? 'bg-purple-50' : ''
                            }`}>
                              {String(row[col] || '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Save as Template */}
              <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <Save size={20} className="text-amber-600" />
                <div className="flex-1">
                  <Label>Spara som mall (t.ex. "Danskebank", "Revolut")</Label>
                  <Input 
                    placeholder="Mallnamn..."
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Saved Templates List */}
              {templates.length > 0 && (
                <div className="space-y-2">
                  <Label>Sparade mallar</Label>
                  <div className="flex flex-wrap gap-2">
                    {templates.map(t => (
                      <Badge key={t.id} variant="secondary" className="flex items-center gap-1">
                        {t.name}
                        <button 
                          onClick={() => handleDeleteTemplate(t.id)}
                          className="ml-1 hover:text-red-500"
                        >
                          <X size={12} />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setColumnMappingOpen(false); setPendingFile(null); }}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleImportWithMapping}
              disabled={uploading || !columnMapping.date_column || !columnMapping.amount_column}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {uploading ? <RefreshCw className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-2" />}
              Importera transaktioner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single Match Dialog */}
      <Dialog open={matchDialogOpen} onOpenChange={setMatchDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('bankImport.matchTransaction')}</DialogTitle>
            <DialogDescription>
              {t('bankImport.selectInvoice')}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm font-medium">{selectedTransaction.description}</p>
                <p className={`text-lg font-bold ${selectedTransaction.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(selectedTransaction.amount)}
                </p>
                <p className="text-xs text-slate-500">{selectedTransaction.date}</p>
              </div>

              {selectedTransaction.amount > 0 && invoices.length > 0 && (
                <div className="space-y-2">
                  <Label>{t('bankImport.matchWithInvoice')}</Label>
                  <Select onValueChange={(invoiceId) => handleMatch(selectedTransaction.id, invoiceId, null)}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('bankImport.selectInvoice')} />
                    </SelectTrigger>
                    <SelectContent>
                      {invoices.map(inv => (
                        <SelectItem key={inv.id} value={inv.id}>
                          {inv.invoice_number} - {inv.customer_name} - {formatCurrency(inv.total)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>{t('bankImport.bookOnAccount')}</Label>
                <Select onValueChange={(acc) => handleMatch(selectedTransaction.id, null, acc)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('bankImport.selectAccount')} />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.slice(0, 50).map(acc => (
                      <SelectItem key={acc.number} value={acc.number}>
                        {acc.number} - {acc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedTransaction.suggested_account && (
                <div className="pt-2">
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => handleMatch(selectedTransaction.id, null, selectedTransaction.suggested_account)}
                  >
                    {t('bankImport.useSuggestion')}: {selectedTransaction.suggested_account} - {selectedTransaction.suggested_account_name}
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setMatchDialogOpen(false)}>{t('common.cancel')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Match Dialog */}
      <Dialog open={bulkMatchOpen} onOpenChange={setBulkMatchOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckSquare size={20} />
              Bokför {selectedTransactions.size} transaktioner
            </DialogTitle>
            <DialogDescription>
              Välj konto att bokföra alla valda transaktioner på
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Konto *</Label>
              <Select value={bulkAccount} onValueChange={setBulkAccount}>
                <SelectTrigger>
                  <SelectValue placeholder="Välj konto..." />
                </SelectTrigger>
                <SelectContent>
                  {accounts.slice(0, 100).map(acc => (
                    <SelectItem key={acc.number} value={acc.number}>
                      {acc.number} - {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Beskrivning (valfritt)</Label>
              <Input 
                placeholder="Egen beskrivning för verifikaten..."
                value={bulkDescription}
                onChange={(e) => setBulkDescription(e.target.value)}
              />
            </div>

            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="create-rule"
                  checked={createRule}
                  onCheckedChange={setCreateRule}
                />
                <Label htmlFor="create-rule" className="flex items-center gap-2 cursor-pointer">
                  <Sparkles size={16} className="text-purple-600" />
                  Skapa automatiseringsregel
                </Label>
              </div>
              
              {createRule && (
                <div className="space-y-3 pt-2">
                  <div className="space-y-1">
                    <Label className="text-sm">Regelnamn</Label>
                    <Input 
                      placeholder="t.ex. Lönebetalningar"
                      value={ruleName}
                      onChange={(e) => setRuleName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Mönster att matcha</Label>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={suggestRulePattern}
                        className="text-xs"
                      >
                        Föreslå från valda
                      </Button>
                    </div>
                    <Input 
                      placeholder="t.ex. löneutbetalning"
                      value={rulePattern}
                      onChange={(e) => setRulePattern(e.target.value)}
                    />
                    <p className="text-xs text-slate-500">
                      Alla framtida transaktioner med detta mönster bokförs automatiskt
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkMatchOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleBulkMatch}
              disabled={!bulkAccount}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Check size={16} className="mr-2" />
              Bokför alla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Automation Rules Dialog */}
      <Dialog open={rulesDialogOpen} onOpenChange={setRulesDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap size={20} />
              Automatiseringsregler
            </DialogTitle>
            <DialogDescription>
              Skapa regler för att automatiskt bokföra transaktioner baserat på beskrivning
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Add/Edit Rule Form */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Plus size={16} />
                {editingRule ? 'Redigera regel' : 'Ny regel'}
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Namn *</Label>
                  <Input 
                    placeholder="t.ex. Lönebetalningar"
                    value={newRule.name}
                    onChange={(e) => setNewRule({...newRule, name: e.target.value})}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Mönster *</Label>
                  <Input 
                    placeholder="t.ex. löneutbetalning"
                    value={newRule.pattern}
                    onChange={(e) => setNewRule({...newRule, pattern: e.target.value})}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Matchningstyp</Label>
                  <Select value={newRule.pattern_type} onValueChange={(v) => setNewRule({...newRule, pattern_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contains">Innehåller</SelectItem>
                      <SelectItem value="starts_with">Börjar med</SelectItem>
                      <SelectItem value="exact">Exakt</SelectItem>
                      <SelectItem value="regex">Regex</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Konto *</Label>
                  <Select value={newRule.account_number} onValueChange={(v) => setNewRule({...newRule, account_number: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Välj konto..." />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.slice(0, 100).map(acc => (
                        <SelectItem key={acc.number} value={acc.number}>
                          {acc.number} - {acc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button onClick={handleSaveRule} className="bg-indigo-600 hover:bg-indigo-700">
                  <Save size={14} className="mr-2" />
                  {editingRule ? 'Uppdatera' : 'Spara regel'}
                </Button>
                {editingRule && (
                  <Button variant="outline" onClick={() => {
                    setEditingRule(null);
                    setNewRule({ name: "", pattern: "", pattern_type: "contains", account_number: "" });
                  }}>
                    Avbryt
                  </Button>
                )}
              </div>
            </div>

            {/* Rules List */}
            <div className="space-y-2">
              <h4 className="font-medium">Befintliga regler ({automationRules.length})</h4>
              {automationRules.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Inga regler skapade än</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {automationRules.map(rule => (
                    <div 
                      key={rule.id} 
                      className={`p-3 rounded-lg border flex items-center justify-between ${
                        rule.is_active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{rule.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {rule.pattern_type === 'contains' ? 'Innehåller' : 
                             rule.pattern_type === 'starts_with' ? 'Börjar med' :
                             rule.pattern_type === 'exact' ? 'Exakt' : 'Regex'}
                          </Badge>
                          <Badge className="bg-slate-100 text-slate-600 text-xs">
                            {rule.matches_count} matchningar
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">
                          "{rule.pattern}" → {rule.account_number} {rule.account_name}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => toggleRuleActive(rule)}
                          className={rule.is_active ? 'text-green-600' : 'text-slate-400'}
                        >
                          <Power size={14} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setEditingRule(rule);
                            setNewRule({
                              name: rule.name,
                              pattern: rule.pattern,
                              pattern_type: rule.pattern_type,
                              account_number: rule.account_number
                            });
                          }}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500"
                          onClick={() => handleDeleteRule(rule.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRulesDialogOpen(false)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankImport;
